<template>
	<ArkHoverCard
		:positioning="{ placement: 'right-start', gutter: 24, flip: false, strategy: 'fixed' }"
		:open-delay="150"
		:close-delay="150"
	>
		<template #trigger>
			<button type="button" class="info-trigger" :aria-label="t('layers.info-popover.trigger-label')">
				<AppIcon :name="IconName.Info" :size="15" :stroke-width="1.6" />
			</button>
		</template>
		<div class="info-content">
			<p class="description">{{ description }}</p>
			<p v-if="source" class="source">{{ t("layers.info-popover.source") }} {{ source }}</p>
			<div v-if="legend?.length" class="legend-block">
				<p class="legend-title">
					{{ t("layers.info-popover.legend-title") }}<template v-if="legendLabel"> — {{ legendLabel }}</template>
				</p>
				<ul class="legend">
					<li v-for="item in legend" :key="item.label" class="legend-row">
						<img v-if="item.icon" :src="item.icon" alt="" class="icon" />
						<span
							v-else-if="item.line"
							class="line-swatch"
							:class="{ dashed: item.line.dashed }"
							:style="{ opacity: item.line.opacity }"
						/>
						<span v-else class="swatch" :style="{ background: item.color }" />
						<span>{{ item.label }}</span>
					</li>
				</ul>
			</div>
		</div>
	</ArkHoverCard>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n"
import ArkHoverCard from "@/components/ui/ArkHoverCard.vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"

interface LayerInfoLegendItem {
	label: string
	color?: string
	icon?: string
	line?: { dashed: boolean; opacity: number }
}

defineProps<{
	description: string
	source?: string
	legend?: LayerInfoLegendItem[]
	legendLabel?: string
}>()

const { t } = useI18n()
</script>

<style scoped>
:deep(.content) {
	border-color: var(--neutral-200);
	box-shadow: var(--shadow-xl);
	z-index: 1100;
}

.info-trigger {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: var(--size-icon-xl);
	height: var(--size-icon-xl);
	border: none;
	background: none;
	color: var(--neutral-500);
	cursor: pointer;
	border-radius: var(--border-radius-base);
	transition:
		color 0.2s ease,
		background-color 0.2s ease;

	&:hover,
	&:focus-visible {
		color: var(--neutral-900);
		background-color: var(--neutral-100);
	}
}

.info-content {
	padding: var(--spacing-sm) var(--spacing-md);
	max-width: var(--width-map-popup);
	display: flex;
	flex-direction: column;
	gap: var(--spacing-xs);

	.description {
		margin: 0;
		font-size: var(--font-size-small);
		color: var(--neutral-900);
		line-height: var(--line-height-normal);
	}

	.source {
		margin: 0;
		font-size: var(--font-size-xxs);
		color: var(--neutral-600);
	}

	.legend-block {
		margin-top: var(--spacing-xxs);
		padding: var(--spacing-sm);
		border-radius: var(--border-radius-base);
		background: var(--neutral-50);
	}

	.legend-title {
		margin: 0 0 var(--spacing-xs);
		font-size: var(--font-size-xxs);
		font-weight: var(--font-weight-semibold);
		text-transform: uppercase;
		letter-spacing: 0.04em;
		color: var(--neutral-600);
	}

	.legend {
		list-style: none;
		margin: 0;
		padding: 0;
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
	}

	.legend-row {
		display: flex;
		align-items: center;
		gap: var(--spacing-xs);
		font-size: var(--font-size-small);
		color: var(--neutral-800);

		.swatch {
			width: var(--space-12);
			height: var(--space-12);
			border-radius: var(--border-radius-sm);
			flex-shrink: 0;
		}

		.line-swatch {
			width: var(--space-20);
			height: 0;
			border-top: var(--border-width-base) solid var(--neutral-700);
			flex-shrink: 0;

			&.dashed {
				border-top-style: dashed;
			}
		}

		.icon {
			width: var(--size-icon-md);
			height: var(--size-icon-md);
			flex-shrink: 0;
		}
	}
}
</style>
